import { getDefaultLocale, Locale, urlWithLocale } from '@tuic/i18n';
import { toQueryString } from '@tuic/lib/src/helpers/to-query-string';
import { HOST_APP_NEXTGEN, HOST_IBE } from '@tuic/lib/urls';
import { ProductWithServices, Tariff } from '../types';
import {
  findFirstCruiseService,
  findFirstInboundFlightService,
  findFirstOutboundFlightService,
} from '../filters';
import { getPartyCompositionFromProduct } from './passengers';
import type { ApiKitTrip } from '@tuic/api-kit';
import { isTariff } from '../guards';

type BookingUrlArg = {
  locale: Locale;
  product: ProductWithServices | undefined;
  backLink?: string;
  webview?: boolean;
  queueToken?: string;
};

export const getBookingUrl = ({
  locale,
  product,
  ...params
}: BookingUrlArg): string | undefined => {
  if (typeof product === 'undefined') {
    return undefined;
  }

  let cruiseService = findFirstCruiseService(product);

  if (!cruiseService) {
    return undefined;
  }

  let [adults, children] = getPartyCompositionFromProduct(product);

  let queryString = toQueryString({
    locale: locale ?? getDefaultLocale(),
    tripCode: cruiseService?.journeyIdentifier,
    ePackageCode: cruiseService?.packageReference,
    adults: adults,
    childs: children,
    cabinType: cruiseService?.variant?.cabin.cabinCode.substring(0, 4),
    shipBoundAirport: findFirstOutboundFlightService(product)?.departureAirport,
    homeBoundAirport: findFirstInboundFlightService(product)?.arrivalAirport,
    jt: params?.queueToken,
    backLink: params?.backLink,
    inAppView: params?.webview ? '1' : null,
  });

  return `${HOST_IBE}?${queryString}`;
};

type GetJourneyUrlArg = {
  product: ProductWithServices | undefined;
  trip?: Pick<ApiKitTrip, 'code' | 'slug'>;
  locale?: Locale;
  adults?: number | string;
  children?: number | string;
  cabinType?: string;
  cabinCategoryType?: string;
  priceMode?: string;
  tariff?: string;
  inboundAirport?: string;
  outboundAirport?: string;
  [key: string]: any;
};

export const getJourneyUrl = ({
  locale,
  product,
  trip,
  ...params
}: GetJourneyUrlArg): string | undefined => {
  const fallbackUrl =
    trip && trip.slug && trip.code
      ? urlWithLocale(locale, `/trips/${trip.slug}-${trip.code}`)
      : undefined;

  if (typeof product === 'undefined') {
    return fallbackUrl;
  }

  let cruiseService = findFirstCruiseService(product);

  if (!cruiseService) {
    return fallbackUrl;
  }

  let [adults, children] = getPartyCompositionFromProduct(product);

  let slug = trip
    ? `${trip.slug}-${trip.code}`
    : cruiseService?.journeyIdentifier;

  const url = urlWithLocale(locale, `/trips/${slug}`);

  let priceMode = params?.priceMode;

  if (priceMode !== 'total' && priceMode !== 'perPerson') {
    priceMode = undefined;
  }

  let variant = cruiseService.variant;
  let tariff = params.tariff ?? variant?.cabin.tariffType;
  let cabinType = variant?.cabin.cabinType;

  const queryString = toQueryString({
    adults: adults !== 2 ? adults : null,
    children: children !== 0 ? children : null,
    cabinType: cabinType !== 'Interior' ? cabinType : null,
    cabinCategoryType: variant?.cabin.cabinCode,
    inboundAirport: findFirstInboundFlightService(product)?.arrivalAirport,
    outboundAirport: findFirstOutboundFlightService(product)?.departureAirport,
    ...params,
    priceMode,
    tariff: isTariff(tariff) ? tariff : null,
  });

  if (queryString === '') {
    return url;
  }

  return `${url}?${queryString}`;
};

export function getJourneyUrlAbsolute(
  args: GetJourneyUrlArg,
): string | undefined {
  return `${HOST_APP_NEXTGEN}${getJourneyUrl(args)}`;
}
